"use client";

import { useState } from "react";
import Icon from "@/components/ui/AppIcon";

interface CIDIFLYStep {
  id: string;
  label: string;
  icon: string;
  headline: string;
  bullets: string[];
}

const steps: CIDIFLYStep[] = [
  {
    id: "context",
    label: "Context",
    icon: "DatabaseIcon",
    headline: "Context: Systems & Process Scope",
    bullets: [
      "Map all source/target systems",
      "Define business process boundaries",
      "Identify data ownership",
    ],
  },
  {
    id: "impact",
    label: "Impact",
    icon: "BoltIcon",
    headline: "Impact: What Breaks if This Fails?",
    bullets: [
      "Financial posting errors",
      "Compliance violations",
      "Operational disruptions",
    ],
  },
  {
    id: "differentiation",
    label: "Differentiation",
    icon: "LightBulbIcon",
    headline: "Differentiation: Why Is This Hard?",
    bullets: [
      "ERP-specific data models",
      "Real-time vs. batch trade-offs",
      "Partner network complexity",
    ],
  },
  {
    id: "illustration",
    label: "Illustration/Demo",
    icon: "PresentationChartLineIcon",
    headline: "Illustration: Show, Don't Tell",
    bullets: [
      "Sample payload structures",
      "Flow diagrams (happy path + errors)",
      "Reference architecture",
    ],
  },
  {
    id: "flow",
    label: "Flow",
    icon: "ArrowPathIcon",
    headline: "Flow: End-to-End Steps",
    bullets: [
      "Inbound validation",
      "Transformation logic",
      "Outbound delivery + retry",
    ],
  },
  {
    id: "leverage",
    label: "Leverage",
    icon: "CubeIcon",
    headline: "Leverage: Reusable Assets",
    bullets: [
      "Standard patterns library",
      "Pre-built connectors",
      "Testing frameworks",
    ],
  },
  {
    id: "yield",
    label: "Yield/You Ask",
    icon: "ChatBubbleLeftRightIcon",
    headline: "Yield: What Outcomes Do You Want?",
    bullets: [
      "Faster go-live?",
      "Lower TCO?",
      "Better observability?",
    ],
  },
];

export default function CIDIFLYTabs() {
  const [activeStep, setActiveStep] = useState("context");

  const currentStep = steps.find((step) => step.id === activeStep) || steps[0];

  return (
    <div className="w-full">
      {/* Tabs */}
      <div className="flex flex-wrap gap-2 mb-8">
        {steps.map((step) => (
          <button
            key={step.id}
            onClick={() => setActiveStep(step.id)}
            className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
              activeStep === step.id
                ? "bg-blue-500 text-white shadow-lg"
                : "bg-white/5 text-neutral-400 border border-white/10 hover:bg-white/10"
            }`}
          >
            {step.label}
          </button>
        ))}
      </div>

      {/* Active Tab Content */}
      <div className="glass-card rounded-2xl p-8 shadow-xl">
        <div className="flex items-center gap-3 mb-6">
          <Icon name={currentStep.icon as any} size={32} className="text-blue-400" />
          <h3 className="text-2xl font-semibold text-white">{currentStep.headline}</h3>
        </div>
        <ul className="space-y-3 mb-6">
          {currentStep.bullets.map((bullet, index) => (
            <li key={`bullet_${index}`} className="flex items-start gap-3">
              <span className="text-blue-400 mt-1">•</span>
              <span className="text-neutral-300">{bullet}</span>
            </li>
          ))}
        </ul>
        {/* Miniature Diagram Placeholder */}
        <div className="w-full h-48 bg-neutral-800/50 border border-white/10 rounded-lg flex items-center justify-center">
          <span className="text-neutral-500 text-sm">
            [Diagram: {currentStep.label} Flow]
          </span>
        </div>
      </div>
    </div>
  );
}